import {MapService} from '@/services/MapService';
import {black, white} from '@/lib/colors';
import {TextOverlayView, type TextOverlayViewOptionsBase} from '../TextOverlayView/TextOverlayView';
import {createLabelGraphic} from './LabelGraphic';
import type {DisplayField} from '@/lib/classes/InfoWindowContent/DisplayField';
import {linkedLabelsOf} from '@/components/FeatureMenuItems/Folders/linkedLabelNesting';

const PLACEMENT_GAP_PX = 16;

export interface LinkedLabelLink {
  featureId: string;
  /** Labels of the source feature's display fields shown on the label. */
  fieldLabels: string[];
}

export interface LinkedLabelSource {
  id: string;
  name: string | null;
  bounds: google.maps.LatLngBoundsLiteral;
  displayFields: DisplayField[];
  /** Only polygon-like graphics carry a `color`; it is absent when the user can't pick one. */
  graphic: Promise<{color?: string | null}> | {color?: string | null};
  parent: {newFeature(data: object): Promise<unknown>};
}

export interface LinkedLabelTarget {
  linkedLabel: LinkedLabelLink;
  graphic: Promise<{text: string; setLinkedFields(text: string, fieldLabels: string[]): void}>;
}

export interface LinkedLabelRefreshSource {
  id: string;
  displayFields: DisplayField[];
  parent: {features: Map<string, {id: string; linkedLabel?: LinkedLabelLink | null; graphic?: LinkedLabelTarget['graphic']}>};
}

type LinkedLabelColors = Required<Pick<TextOverlayViewOptionsBase, 'backgroundColor' | 'borderColor' | 'textColor'>>;

/** Fields without a value are skipped so the label has no blank lines. */
export function buildLinkedLabelText(displayFields: DisplayField[], fieldLabels: string[]): string {
  const selected = new Set(fieldLabels);
  return displayFields
    .filter((field) => selected.has(field.label))
    .map((field) => field.toValueString().trim())
    .filter((value) => value.length > 0)
    .join('\n');
}

export function linkedLabelColors(featureColor?: string | null): LinkedLabelColors {
  if (featureColor) {
    return {backgroundColor: featureColor, borderColor: featureColor, textColor: white};
  }
  return {backgroundColor: white, borderColor: black, textColor: black};
}

export function linkedLabelCenter(
  featureBounds: google.maps.LatLngBoundsLiteral,
  text: string,
): google.maps.LatLng | google.maps.LatLngLiteral {
  const middleLat = (featureBounds.north + featureBounds.south) / 2;
  const eastEdge = MapService.latLngToScreenPixel({lat: middleLat, lng: featureBounds.east});
  const {width} = TextOverlayView.estimateNewLabelSize(text);
  return MapService.screenPixelToLatLng(eastEdge.x + PLACEMENT_GAP_PX + (width / 2), eastEdge.y);
}

export async function createLinkedLabel(source: LinkedLabelSource, fieldLabels: string[]): Promise<unknown> {
  const text = buildLinkedLabelText(source.displayFields, fieldLabels);
  if (!text) {
    throw new Error(`None of the selected fields on feature ${source.id} have a value to show on a label.`);
  }

  const {color} = await source.graphic;
  const worldWidth = await MapService.getWorldWidth();
  const label = createLabelGraphic({
    center: linkedLabelCenter(source.bounds, text),
    worldWidth,
    text,
    ...linkedLabelColors(color),
    linkedLabel: {featureId: source.id, fieldLabels},
  });

  const name = source.name ? `${source.name} Label` : 'Label';
  return await source.parent.newFeature({...label.toJSON(), name});
}

export async function updateLinkedLabel(
  source: LinkedLabelSource,
  label: LinkedLabelTarget,
  fieldLabels: string[],
): Promise<void> {
  const text = buildLinkedLabelText(source.displayFields, fieldLabels);
  if (!text) {
    throw new Error(`None of the selected fields on feature ${source.id} have a value to show on a label.`);
  }

  const graphic = await label.graphic;
  graphic.setLinkedFields(text, fieldLabels);
  // Keep the Feature's read cache in step with the graphic, which saves it
  label.linkedLabel = {featureId: source.id, fieldLabels};
}

export async function refreshLinkedLabels(source: LinkedLabelRefreshSource): Promise<void> {
  const labels = linkedLabelsOf(source.parent.features, source.id);
  await Promise.all(labels.map(async ({linkedLabel, graphic}) => {
    if (!linkedLabel || !graphic) return;
    const text = buildLinkedLabelText(source.displayFields, linkedLabel.fieldLabels);
    // A label whose fields no longer have values keeps its last text rather than going blank
    if (!text) return;
    const labelGraphic = await graphic;
    if (text !== labelGraphic.text) {
      labelGraphic.setLinkedFields(text, linkedLabel.fieldLabels);
    }
  }));
}
